import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { timeOptions } from "@plasma/data/time";
import { formatDateUTC } from "@plasma/utils/time";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Calendar } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "~/components/ui/input-group";
import { Label } from "~/components/ui/label";
import { SelectCombobox } from "~/components/ui/select-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";

const updateShiftRef = api.employeeShifts.update;

// V4.1: Route-based modal for editing employee shifts
// Search params pass shift data to avoid an additional fetch
const searchParamsSchema = z.object({
  employeeName: z.string(),
  employeeImageUrl: z.string().optional(),
  locationName: z.string(),
  date: z.string(),
  startTime: z.string(),
  endTime: z.string(),
});

export const Route = createFileRoute(
  "/_authenticated/employees/shifts/$shiftId/edit"
)({
  component: EditShiftModal,
  validateSearch: searchParamsSchema,
});

const roundTime = (time: string) => {
  const [hour, minute] = time.split(":");
  const minuteValue = minute ?? "0";
  const roundedMinute =
    Math.floor(Number.parseInt(minuteValue, 10) / 15 + 0.5) * 15;
  return `${hour}:${roundedMinute.toString().padStart(2, "0")}`;
};

interface EditFormData {
  startTime: string;
  endTime: string;
}

function EditShiftModal() {
  const navigate = useNavigate();
  const { shiftId } = Route.useParams();
  const {
    employeeName,
    employeeImageUrl: _employeeImageUrl,
    locationName,
    date,
    startTime,
    endTime,
  } = Route.useSearch();

  const handleClose = () => {
    void navigate({ to: "/employees/shifts" });
  };

  const editShiftMutation = useMutation({
    mutationFn: ({
      employeeShiftId,
      data,
    }: {
      employeeShiftId: Id<"employeeShifts">;
      data: { startTime: string; endTime: string };
    }) => {
      return convexClient.mutation(updateShiftRef, {
        employeeShiftId,
        data,
      });
    },
    onSuccess: () => {
      ProductEvents.shiftUpdated(shiftId);
      handleClose();
    },
  });

  const form = useForm<EditFormData>({
    values: {
      startTime: timeConvert24hrTo12hr(roundTime(startTime)),
      endTime: timeConvert24hrTo12hr(roundTime(endTime)),
    },
  });

  const handleSubmit = async (data: EditFormData) => {
    if (editShiftMutation.isPending) {
      return;
    }
    await editShiftMutation.mutateAsync({
      employeeShiftId: toConvexTableId("employeeShifts", shiftId),
      data: {
        startTime: data.startTime,
        endTime: data.endTime,
      },
    });
    form.reset();
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open>
      <DialogContent className="max-w-lg" noScrollPrevention>
        <DialogHeader>
          <DialogTitle className="h4 text-foreground text-center">
            Edit Employee Shift
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-center">
            Edit shift record for an employee.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <div className="space-y-2">
              <Label>Date</Label>
              <InputGroup>
                <InputGroupAddon align="inline-start">
                  <Calendar className="text-muted-foreground size-5" />
                </InputGroupAddon>
                <InputGroupInput
                  defaultValue={formatDateUTC(
                    new Date(Number(date)),
                    "MMM D, YYYY"
                  )}
                  disabled
                />
              </InputGroup>
            </div>
            <div className="space-y-5">
              <Label>Employee</Label>
              <Input defaultValue={employeeName} disabled />
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input defaultValue={locationName} disabled />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <SelectCombobox
                        onChange={field.onChange}
                        options={timeOptions}
                        value={field.value ?? null}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <SelectCombobox
                        onChange={field.onChange}
                        options={timeOptions}
                        value={field.value ?? null}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              className="w-full"
              disabled={editShiftMutation.isPending}
              size="lg"
              type="submit"
            >
              Save
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
